import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Skeleton,
} from "@mui/material";

import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import HistoryIcon from "@mui/icons-material/History";

import AddToWatchlistDialog from "./AddToWatchlistDialog";
import { getCurrencySymbol, fmtL } from "../../../utils/currency";

/* ================= COMPONENT ================= */
const sym = getCurrencySymbol();
const market = sessionStorage.getItem("selectedMarket") ?? "US";

const RecentlyViewedWidget = ({ data = [], loading }) => {
  const [openWatchlist, setOpenWatchlist] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState("");
  const [snackbar, setSnackbar] = useState(null);

  const handleOpenWatchlist = (ticker) => {
    setSelectedTicker(ticker);
    setOpenWatchlist(true);
  };

  return (
    <>
      {/* ================= WIDGET ================= */}
      <Box
        sx={{
          bgcolor: "var(--color-surface)",
          border: "1px solid var(--color-border-primary)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            bgcolor: "var(--color-bg-secondary)",
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: "var(--color-surface-hover)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HistoryIcon sx={{ fontSize: 16, color: "var(--color-info)" }} />
            </Box>
            <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)">
              Recently viewed
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 2,
            }}
          >
            <Typography
              fontSize="var(--font-size-xs2)"
              fontWeight={600}
              color="var(--color-text-secondary)"
              textTransform="uppercase"
              letterSpacing="0.03em"
            >
              Symbol
            </Typography>
            <Typography
              fontSize="var(--font-size-xs2)"
              fontWeight={600}
              color="var(--color-text-secondary)"
              textTransform="uppercase"
              letterSpacing="0.03em"
              align="right"
            >
              Price
            </Typography>
          </Box>
        </Box>

        {/* BODY */}
        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {loading && (
            <Box p={2}>
              {[...Array(3)].map((_, i) => (
                <Box key={i} mb={1.5}>
                  <Skeleton
                    height={48}
                    sx={{
                      borderRadius: 1,
                      bgcolor: "var(--color-surface-hover)",
                      "&::after": {
                        background: "linear-gradient(90deg, transparent, var(--color-surface-active), transparent)",
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}

          {!loading && data.length === 0 && (
            <Box py={6} textAlign="center">
              <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" fontWeight={500}>
                No recently viewed stocks
              </Typography>
            </Box>
          )}

          {!loading &&
            data.slice(0, 5).map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom:
                    idx < data.length - 1
                      ? "1px solid var(--color-border-primary)"
                      : "none",
                  "&:hover": {
                    bgcolor: "var(--color-surface-hover)",
                    transition: "var(--transition-fast)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {/* SYMBOL + ADD */}
                  <Box display="flex" alignItems="center" gap={1}>
                    {market !== "ALL" &&
                      <IconButton
                        size="small"
                        onClick={() => handleOpenWatchlist(item.ticker)}
                        sx={{
                          color: "var(--color-text-secondary)",
                          "&:hover": {
                            bgcolor: "var(--color-surface-active)",
                            color: "var(--color-info)",
                          },
                        }}
                      >
                        <BookmarkBorderIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    }
                    <Box minWidth={0}>
                      <Typography
                        fontSize="var(--font-size-sm)"
                        fontWeight={600}
                        color="var(--color-text-primary)"
                      >
                        {item.ticker}
                      </Typography>
                      <Typography
                        fontSize="var(--font-size-xs2)"
                        color="var(--color-text-secondary)"
                        noWrap
                        sx={{ maxWidth: 180 }}
                      >
                        {item.company_name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* PRICE */}
                  <Typography
                    fontSize="var(--font-size-sm)"
                    fontWeight={600}
                    align="right"
                    color="var(--color-text-primary)"
                  >
                    {sym}{fmtL(item.current_price || 0, 2)}
                  </Typography>
                </Box>
              </Box>
            ))}
        </Box>
      </Box>

      {/* ================= ADD TO WATCHLIST DIALOG ================= */}
      <AddToWatchlistDialog
        open={openWatchlist}
        onClose={() => setOpenWatchlist(false)}
        ticker={selectedTicker}
      />

      {/* ================= SNACKBAR ================= */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        message={snackbar?.message}
        onClose={() => setSnackbar(null)}
        ContentProps={{
          sx: {
            fontSize: 13,
            bgcolor: "var(--color-surface)",
            color: "var(--color-text-primary)",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--color-border-primary)",
          },
        }}
      />
    </>
  );
};

export default RecentlyViewedWidget;